import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { customerTokenHandler } from '../utils/tokenExpirationHandler';
import { buildApiErrorMessage } from '../utils/apiErrorUtils';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

/** Same-token verification deduped — magic links are single-use; concurrent calls would invalidate the link on the 2nd POST. */
const magicLinkVerifyInFlight = new Map();

export const AuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing auth data on app load
    // Skip if we're on the verify page - let VerifyPage handle auth state
    const isVerifyPage = window.location.pathname === '/auth/verify';
    
    if (isVerifyPage) {
      // On verify page, don't load any auth state and set loading to false immediately
      setIsLoading(false);
      return;
    }
    
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('customerToken');
      const storedRefreshToken = localStorage.getItem('customerRefreshToken');
      const storedCustomer = localStorage.getItem('customerData');

      if (storedToken && storedCustomer) {
        try {
          // Parse customer data first
          const parsedCustomer = JSON.parse(storedCustomer);
          
          // Set all state values in sequence to ensure they're set before we mark loading as complete
          // Using Promise to ensure state updates are queued properly
          await Promise.resolve().then(() => {
            setToken(storedToken);
            setRefreshToken(storedRefreshToken);
            setCustomer(parsedCustomer);
          });
          
          // Add a small delay to ensure state updates have been processed by React
          // This prevents the race condition where isLoading becomes false before state is ready
          await new Promise(resolve => setTimeout(resolve, 0));
        } catch (error) {
          console.error('Error parsing stored auth data:', error);
          // Clear corrupted auth data
          localStorage.removeItem('customerToken');
          localStorage.removeItem('customerRefreshToken');
          localStorage.removeItem('customerData');
          localStorage.removeItem('customerCurrentRoute');
          setToken(null);
          setRefreshToken(null);
          setCustomer(null);
        }
      }
      
      // Set loading to false only after state initialization is complete
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const requestMagicLink = async (email) => {
    try {
      const response = await fetch(`${API_BASE}/customer/auth/request-magic-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Check your email for a login link!');
        return { success: true };
      } else {
        toast.error(data.message || 'Failed to send login link');
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Magic link request error:', error);
      toast.error('Failed to send login link. Please try again.');
      return { success: false, message: 'Network error' };
    }
  };

  const verifyMagicLink = async (token) => {
    const normalized =
      typeof token === 'string' ? token.trim() : '';
    if (!normalized) {
      return { success: false, message: 'Token is required' };
    }

    const existing = magicLinkVerifyInFlight.get(normalized);
    if (existing) {
      return existing;
    }

    const promise = (async () => {
      try {
        setIsLoading(true);

        const response = await fetch(`${API_BASE}/customer/auth/verify-magic-link`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token: normalized })
        });

        const data = await response.json();

        if (data.success) {
          const { token: sessionToken, refreshToken: newRefreshToken, customer } = data.data;

          localStorage.setItem('customerToken', sessionToken);
          localStorage.setItem('customerRefreshToken', newRefreshToken);
          localStorage.setItem('customerData', JSON.stringify(customer));

          setToken(sessionToken);
          setRefreshToken(newRefreshToken);
          setCustomer(customer);

          return { success: true };
        } else {
          console.error('Magic link verification failed:', data.message);
          return { success: false, message: data.message || 'Invalid or expired login link' };
        }
      } catch (error) {
        console.error('Magic link verification error:', error);
        return { success: false, message: 'Failed to verify login link. Please try again.' };
      } finally {
        setIsLoading(false);
        magicLinkVerifyInFlight.delete(normalized);
      }
    })();

    magicLinkVerifyInFlight.set(normalized, promise);
    return promise;
  };

  const refreshAccessToken = async () => {
    if (!refreshToken) {
      customerTokenHandler.handleTokenExpiration(logout);
      return false;
    }

    try {
      const response = await customerTokenHandler.fetchWithExpirationHandling(
        `${API_BASE}/customer/auth/refresh-token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ refreshToken })
        },
        logout
      );

      const data = await response.json();
      
      if (data.success) {
        const { token: newToken, customer } = data.data;
        
        localStorage.setItem('customerToken', newToken);
        localStorage.setItem('customerData', JSON.stringify(customer));
        
        setToken(newToken);
        setCustomer(customer);
        
        return true;
      } else {
        customerTokenHandler.handleTokenExpiration(logout);
        return false;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      customerTokenHandler.handleTokenExpiration(logout);
      return false;
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await fetch(`${API_BASE}/customer/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage and state
      localStorage.removeItem('customerToken');
      localStorage.removeItem('customerRefreshToken');
      localStorage.removeItem('customerData');
      localStorage.removeItem('customerCurrentRoute');
      
      setToken(null);
      setRefreshToken(null);
      setCustomer(null);
      
      toast.success('Logged out successfully');
    }
  };

  const makeAuthenticatedRequest = async (url, options = {}) => {
    // Wait for token to be available (handles async state updates on refresh)
    let currentToken = token;
    if (!currentToken) {
      // Try to get token from localStorage as fallback (handles refresh case)
      const storedToken = localStorage.getItem('customerToken');
      if (storedToken) {
        currentToken = storedToken;
      } else {
        throw new Error('No authentication token');
      }
    }

    let authToken = currentToken;

    // Ensure URL starts with API base if it's a relative path
    let fullUrl;
    if (url.startsWith('http')) {
      fullUrl = url;
    } else if (url.startsWith('/parametric/')) {
      // Parametric endpoints go directly to /api/parametric/
      fullUrl = `${API_BASE}${url}`;
    } else {
      // Customer endpoints go to /api/customer/
      fullUrl = `${API_BASE}/customer${url}`;
    }

    const makeRequest = async (authToken) => {
      // Don't set Content-Type for FormData - let the browser set it with boundary
      const headers = {
        'Authorization': `Bearer ${authToken}`,
        ...options.headers
      };
      
      // Only set Content-Type to application/json if body is not FormData
      if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }
      
      // Use direct fetch here so we can handle 401 by attempting refresh first
      return fetch(fullUrl, {
        ...options,
        headers
      });
    };

    let response = await makeRequest(authToken);

    // Check for 401 before parsing response
    if (response.status === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        // Retry the request with new token (get updated token from state or localStorage)
        const newToken = token || localStorage.getItem('customerToken');
        if (newToken) {
          response = await makeRequest(newToken);
          // Check if retry also failed
          if (response.status === 401) {
            customerTokenHandler.handleTokenExpiration(logout);
            throw new Error('Authentication failed');
          }
        } else {
          // No new token available after refresh
          customerTokenHandler.handleTokenExpiration(logout);
          throw new Error('Authentication failed');
        }
      } else {
        // Token refresh failed, handle expiration
        customerTokenHandler.handleTokenExpiration(logout);
        throw new Error('Authentication failed');
      }
    }

    // Parse the response to JSON (only if response is OK or we've handled 401)
    let responseData;
    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        // If response is not JSON, create an error response
        const text = await response.text();
        throw new Error(`Invalid response format: ${text.substring(0, 100)}`);
      }
    } catch (parseError) {
      // If JSON parsing fails, check if it's a network error or invalid response
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      throw parseError;
    }

    // If response is not OK (and not 401 which we handled above), throw error
    if (!response.ok) {
      const message = buildApiErrorMessage(responseData, response.status);
      const err = new Error(message);
      err.status = response.status;
      err.errors = responseData?.errors;
      err.apiResponse = responseData;
      throw err;
    }

    return responseData;
  };

  // Compute isAuthenticated - check both state and localStorage as fallback
  // This prevents the race condition where state hasn't updated yet on refresh
  const isAuthenticated = useMemo(() => {
    // Always check localStorage first for the most reliable source of truth
    // This ensures that even during state transitions, we have the correct auth status
    const storedToken = localStorage.getItem('customerToken');
    const storedCustomer = localStorage.getItem('customerData');
    
    // If we have valid data in localStorage, we're authenticated
    if (storedToken && storedCustomer) {
      try {
        // Verify the stored customer data is valid JSON
        JSON.parse(storedCustomer);
        return true;
      } catch (e) {
        // Invalid JSON, clear everything and return false
        localStorage.removeItem('customerToken');
        localStorage.removeItem('customerRefreshToken');
        localStorage.removeItem('customerData');
        localStorage.removeItem('customerCurrentRoute');
        return false;
      }
    }
    
    // Fallback to state check (for when data is set but not yet in localStorage)
    if (token && customer) {
      return true;
    }
    
    return false;
  }, [token, customer]);

  const value = {
    customer,
    token,
    isAuthenticated,
    isLoading,
    requestMagicLink,
    verifyMagicLink,
    logout,
    makeAuthenticatedRequest
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
